import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { EVENT_EVALUATION_TIMEFRAMES, INSIGHTS_EVENT_TYPES } from "../config/insightsEventTypes";
import ReferenceChartPopup from "./ReferenceChartPopup";
import ReferenceImageViewer from "./ReferenceImageViewer";
import ReferencePackOverview from "./ReferencePackOverview";
import { isEligibleReferenceVersion } from "./referenceAvailability";

const SLOTS = Object.freeze(["S1", "S2", "S3", "F1", "F2", "F3"]);
const DIRECTIONS = Object.freeze(["LONG", "SHORT"]);
const CAPTURE_FIELDS = Object.freeze(["outcomeImageId", "decisionTime", "capturePolicy"]);
const METADATA = Object.freeze([
  ["sourceSymbol", "Source symbol", 80], ["eventDate", "Event date", 10],
  ["outcome", "Outcome", 200], ["mfe", "MFE (%)", 80], ["mae", "MAE (%)", 80], ["note", "Note", 2000],
]);
const latestVersion = (pack) => pack?.versions.reduce((latest, entry) => Math.max(latest, entry.version), 0) || "";
const findPack = (packs, key) => packs.find((pack) => pack.eventType === key.eventType
  && pack.direction === key.direction && pack.timeframe === key.timeframe) || null;
const versionSlots = (pack, version) => pack?.versions.find((entry) => entry.version === Number(version))?.slots || {};
const utc = (value) => new Date(value).toLocaleString(undefined, { timeZone: "UTC" });

function cleanSlots(slots) {
  return Object.fromEntries(SLOTS.filter((slot) => slots[slot]?.imageId).map((slot) => {
    const value = slots[slot];
    return [slot, { imageId: value.imageId,
      ...Object.fromEntries(CAPTURE_FIELDS.filter((key) => value[key] !== undefined).map((key) => [key, value[key]])),
      ...Object.fromEntries(METADATA
      .filter(([key]) => typeof value[key] === "string" && value[key].trim() !== "")
      .map(([key]) => [key, value[key].trim()])) }];
  }));
}

function draftError(slots) {
  for (const slot of SLOTS) {
    const value = slots[slot];
    if (!value) continue;
    for (const [key, label, maximum] of METADATA) {
      if ((value[key] || "").length > maximum) return `${slot}: ${label} must be ${maximum} characters or fewer.`;
    }
    for (const key of ["mfe", "mae"]) {
      const numeric = (value[key] || "").trim();
      if (numeric && (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(numeric) || !Number.isFinite(Number(numeric)))) {
        return `${slot}: ${key.toUpperCase()} must be a number, such as 2.5, or left blank.`;
      }
    }
    if (value.eventDate && (!/^\d{4}-\d{2}-\d{2}$/.test(value.eventDate)
        || Number.isNaN(Date.parse(value.eventDate))
        || new Date(value.eventDate).toISOString().slice(0, 10) !== value.eventDate)) {
      return `${slot}: choose a valid event date (YYYY-MM-DD).`;
    }
  }
  return "";
}

function referenceError(error, action) {
  if (error?.code === "INVALID_ADMIN_RESPONSE") return "The service returned invalid reference data. Reload the latest packs before making changes.";
  if (error?.status === 403) return "This request could not be verified. Reload the page and sign in again.";
  if (error?.status === 413) return `${action} exceeds a service limit. ${error.message} ${action.includes("image upload")
    ? "Try a smaller image (at most 2 MiB); contact the service administrator if storage is full."
    : "Contact the service administrator to review pack history and storage limits."}`;
  if (error?.status === 503) return `Reference pack management is temporarily unavailable. ${error.message || ""} Retry manually in a moment.`;
  if (error?.status === 400) return `${action} was rejected. ${error.message} Review the image limits and metadata, then try again.`;
  return `${action} could not be completed. Check your connection and reload the latest packs before retrying a save.`;
}

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("The image could not be read."));
    reader.onabort = () => reject(new Error("Image reading was cancelled."));
    reader.readAsDataURL(file);
  });
}

function ReferencePreview({ api, imageId, slot, view = "Setup" }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [expanded, setExpanded] = useState(false);
  useLayoutEffect(() => {
    let active = true;
    let objectUrl;
    setPreview(null);
    setError("");
    setExpanded(false);
    if (imageId) api.getReferenceImage(imageId).then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setPreview({ imageId, url: objectUrl });
    }).catch((loadError) => {
      if (active && loadError?.status !== 401) setError("Preview unavailable. Retry to load the saved image.");
    });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, imageId, retry]);
  if (!imageId) return <div className="adminReferenceEmpty">No image uploaded</div>;
  if (error) return <div className="adminReferenceEmpty"><p role="alert">{error}</p>
    <button type="button" className="adminSecondaryButton" onClick={() => setRetry((value) => value + 1)}>Retry {slot} preview</button>
  </div>;
  return preview?.imageId === imageId
    ? <><button type="button" className="adminReferencePreviewButton" aria-haspopup="dialog" aria-expanded={expanded}
      aria-label={`Expand ${slot} ${view.toLowerCase()} preview`} onClick={() => setExpanded(true)}>
      <img className="adminReferencePreview" src={preview.url}
        alt={`${slot} ${view.toLowerCase()} reference image`} />
      <span>Expand {view.toLowerCase()} preview</span>
    </button>{expanded && <ReferenceImageViewer src={preview.url} title={`${slot} · ${view} preview`}
      alt={`${slot} ${view.toLowerCase()} reference image`} onClose={() => setExpanded(false)} />}</>
    : <div className="adminReferenceEmpty" role="status">Loading image…</div>;
}

export default function ReferencePacksPanel({ api, refreshVersion, onRefresh, onDirtyChange, onSavingChange }) {
  const [packs, setPacks] = useState([]);
  const [key, setKey] = useState({ eventType: INSIGHTS_EVENT_TYPES[0].value, direction: "LONG", timeframe: EVENT_EVALUATION_TIMEFRAMES[0] });
  const [selectedVersion, setSelectedVersion] = useState("");
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [captureSlot, setCaptureSlot] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [conflict, setConflict] = useState(null);
  const [reloadRequired, setReloadRequired] = useState(false);
  const keyRef = useRef(key);
  const operationRef = useRef(0);
  const savingRef = useRef(false);
  const editorRef = useRef(null);
  const pack = findPack(packs, key);
  const baseline = versionSlots(pack, selectedVersion);
  const dirty = JSON.stringify(cleanSlots(draft)) !== JSON.stringify(cleanSlots(baseline));
  const validationError = draftError(draft);
  const selected = pack?.versions.find((entry) => entry.version === Number(selectedVersion));
  const retired = !!pack?.retiredVersions.includes(Number(selectedVersion));
  const complete = SLOTS.every((slot) => !!selected?.slots[slot]?.imageId);
  const mutationsBlocked = loading || saving || !!captureSlot || !!conflict || reloadRequired;

  useLayoutEffect(() => () => { operationRef.current += 1; }, []);
  useEffect(() => {
    const operation = ++operationRef.current;
    setLoading(true);
    setError("");
    setMessage("");
    api.getReferencePacks().then((values) => {
      if (operation !== operationRef.current) return;
      const nextPack = findPack(values, keyRef.current);
      const version = latestVersion(nextPack);
      setPacks(values);
      setSelectedVersion(version);
      setDraft(versionSlots(nextPack, version));
      setConflict(null);
      setReloadRequired(false);
    }).catch((loadError) => {
      if (operation !== operationRef.current || loadError?.status === 401) return;
      setError(referenceError(loadError, "Loading packs"));
      setReloadRequired(true);
    }).finally(() => {
      if (operation === operationRef.current) setLoading(false);
    });
    return () => { operationRef.current += 1; };
  }, [api, refreshVersion]);
  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    onSavingChange(saving || !!captureSlot);
    return () => onSavingChange(false);
  }, [saving, captureSlot, onSavingChange]);
  useEffect(() => {
    if (!dirty && !saving && !captureSlot) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, saving, captureSlot]);

  const confirmDiscard = () => !dirty || window.confirm("Discard unsaved reference images and metadata?");
  const openPack = (nextKey, scrollToEditor = false) => {
    if (savingRef.current || loading || !confirmDiscard()) return;
    const nextPack = findPack(packs, nextKey);
    const version = latestVersion(nextPack);
    keyRef.current = nextKey;
    setKey(nextKey);
    setSelectedVersion(version);
    setDraft(versionSlots(nextPack, version));
    setMessage("");
    if (scrollToEditor) {
      editorRef.current?.scrollIntoView({ block: "start" });
      editorRef.current?.querySelector("select")?.focus({ preventScroll: true });
    }
    // A conflict requires a real reload, even if the administrator changes filters.
  };
  const selectKey = (field, value) => openPack({ ...key, [field]: value });
  const selectVersion = (value) => {
    if (savingRef.current || !confirmDiscard()) return;
    setSelectedVersion(Number(value));
    setDraft(versionSlots(pack, value));
    setMessage("");
  };
  const editMetadata = (slot, field, value) => {
    setDraft((current) => ({ ...current, [slot]: { ...current[slot], [field]: value } }));
    setMessage("");
  };
  const beginOperation = () => {
    savingRef.current = true;
    onSavingChange(true);
    setSaving(true);
    setError("");
    setMessage("");
    return ++operationRef.current;
  };
  const finishOperation = (operation) => {
    if (operation !== operationRef.current) return;
    savingRef.current = false;
    setSaving(false);
    setUploading("");
  };
  const upload = async (slot, file) => {
    if (!file || mutationsBlocked || savingRef.current) return;
    if (file.size === 0 || file.size > 2 * 1024 * 1024) {
      setError(`${slot}: choose a non-empty image file no larger than 2 MiB.`);
      return;
    }
    const operation = beginOperation();
    setUploading(slot);
    try {
      const dataBase64 = await readBase64(file);
      if (operation !== operationRef.current) return;
      const image = await api.uploadReferenceImage(dataBase64);
      if (operation !== operationRef.current) return;
      setDraft((current) => {
        const replacement = { ...current[slot], imageId: image.imageId };
        // A manual replacement no longer belongs to the previous paired capture.
        CAPTURE_FIELDS.forEach((field) => { delete replacement[field]; });
        return { ...current, [slot]: replacement };
      });
      setMessage(`${slot} uploaded and normalized to PNG (${image.width} × ${image.height}). Save a new version to include it in the pack.`);
    } catch (uploadError) {
      if (operation === operationRef.current && uploadError?.status !== 401) {
        setError(referenceError(uploadError, `${slot} image upload`));
        if (uploadError?.requiresFreshLoad) setReloadRequired(true);
      }
    } finally { finishOperation(operation); }
  };
  const mutate = async (action, request, createsVersion = false) => {
    if (mutationsBlocked || savingRef.current) return;
    const operation = beginOperation();
    try {
      const value = await request();
      if (operation !== operationRef.current) return;
      setPacks((current) => [...current.filter((entry) => entry.id !== value.id), value]);
      if (createsVersion) {
        const version = latestVersion(value);
        setSelectedVersion(version);
        setDraft(versionSlots(value, version));
      }
      setMessage(`${action} saved. Configure automatic comparison on the Event validation tab.`);
    } catch (saveError) {
      if (operation !== operationRef.current || saveError?.status === 401) return;
      if (saveError?.status === 409 && saveError.current) setConflict(saveError.current);
      else {
        setError(referenceError(saveError, action));
        // A failed response can follow a committed mutation. Reload before retrying.
        if (saveError?.requiresFreshLoad || !saveError?.status || saveError.status >= 500) setReloadRequired(true);
      }
    } finally { finishOperation(operation); }
  };
  const saveCapture = async ({ dataBase64, outcomeDataBase64, sourceSymbol, eventDate, decisionTime, capturePolicy }) => {
    if (!captureSlot || savingRef.current) throw new Error("The selected slot is unavailable.");
    const slot = captureSlot;
    const operation = beginOperation();
    try {
      const image = await api.uploadReferenceImage(dataBase64);
      if (operation !== operationRef.current) throw new Error("The admin session changed.");
      const outcomeImage = await api.uploadReferenceImage(outcomeDataBase64);
      if (operation !== operationRef.current) throw new Error("The admin session changed.");
      setDraft((current) => ({ ...current, [slot]: { ...current[slot], imageId: image.imageId,
        outcomeImageId: outcomeImage.imageId, sourceSymbol, eventDate, decisionTime, capturePolicy } }));
      setMessage(`${slot} setup and outcome images added. Save a new version to keep them in the pack.`);
    } catch (captureError) {
      if (operation === operationRef.current && captureError?.requiresFreshLoad) setReloadRequired(true);
      throw new Error(referenceError(captureError, `${slot} image upload`));
    } finally { finishOperation(operation); }
  };
  const save = () => {
    if ((!dirty && !(retired && selected)) || validationError) return;
    mutate("New reference version", () => api.saveReferencePackVersion(pack?.id || "new", {
      expectedRevision: pack?.revision || 0, ...key, slots: cleanSlots(draft),
    }), true);
  };
  const activate = () => {
    if (!pack || dirty || !complete || retired) return;
    if (pack.activeVersion && pack.activeVersion !== selected.version
        && !window.confirm(`Activate version ${selected.version} and retire the current active version ${pack.activeVersion}?`)) return;
    mutate("Activation", () => api.saveReferencePackStatus(pack.id, {
      expectedRevision: pack.revision, enabled: true, activeVersion: selected.version,
    }));
  };
  const toggleEnabled = () => {
    if (!pack || dirty || (!pack.enabled && !pack.activeVersion)) return;
    mutate(pack.enabled ? "Pack disable" : "Pack enable", () => api.saveReferencePackStatus(pack.id, {
      expectedRevision: pack.revision, enabled: !pack.enabled, activeVersion: pack.activeVersion,
    }));
  };
  const retire = () => {
    if (!pack || !selected || dirty || retired || !window.confirm(`Retire version ${selected.version}?${pack.activeVersion === selected.version ? " This disables the pack and clears its active version." : ""} Historical images remain available.`)) return;
    mutate("Version retirement", () => api.retireReferencePackVersion(pack.id, {
      expectedRevision: pack.revision, version: selected.version,
    }));
  };

  return <div className="adminConfigurationForm">
    <p>REFERENCE validation compares current charts with three successful and three failed setup examples. Packs are shared across symbols; LONG and SHORT have separate examples. Enable REFERENCE on the Event validation tab after activating a complete pack with verified setup captures.</p>
    {loading && <p role="status">Loading reference packs…</p>}
    {error && <div className="adminAlert adminAlertError" role="alert">{error}</div>}
    {conflict && <div className="adminAlert adminAlertWarning" role="alert">
      <p>This pack changed on the server (revision {conflict.revision}, active version {conflict.activeVersion || "none"}, {conflict.enabled ? "enabled" : "disabled"}). Your draft is retained. Reload the latest packs to review server changes before saving again; reloading discards unsaved edits.</p>
    </div>}
    {!loading && !reloadRequired && <ReferencePackOverview packs={packs} selectedKey={key}
      disabled={mutationsBlocked} onOpen={(nextKey) => openPack(nextKey, true)} />}
    <fieldset ref={editorRef} className="adminFieldset adminReferenceFilters" disabled={loading || saving || !!captureSlot}>
      <legend>Find or create a pack</legend>
      <label className="adminFormField"><span>Event type</span>
        <select value={key.eventType} onChange={(event) => selectKey("eventType", event.target.value)}>
          {INSIGHTS_EVENT_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label className="adminFormField"><span>Direction</span>
        <select value={key.direction} onChange={(event) => selectKey("direction", event.target.value)}>
          {DIRECTIONS.map((value) => <option key={value}>{value}</option>)}
        </select>
      </label>
      <label className="adminFormField"><span>Timeframe</span>
        <select value={key.timeframe} onChange={(event) => selectKey("timeframe", event.target.value)}>
          {EVENT_EVALUATION_TIMEFRAMES.map((value) => <option key={value}>{value}</option>)}
        </select>
      </label>
    </fieldset>
    {!loading && <>
      <div className="adminReferenceToolbar">
        {pack ? <label className="adminFormField"><span>View / edit from version</span>
          <select value={selectedVersion} disabled={saving || !!captureSlot} onChange={(event) => selectVersion(event.target.value)}>
            {[...pack.versions].sort((a, b) => b.version - a.version).map((entry) => <option key={entry.version} value={entry.version}>
              Version {entry.version}{entry.version === pack.activeVersion ? " · active" : ""}{pack.retiredVersions.includes(entry.version) ? " · retired" : ""} · {SLOTS.filter((slot) => entry.slots[slot]?.imageId).length}/6 images
            </option>)}
          </select>
        </label> : <p>No saved pack for this selection. Upload an example and save your first version.</p>}
        <div className="adminStatusRow">
          <span className="adminStatusBadge">{pack?.enabled ? "Enabled" : "Disabled"}</span>
          <span className="adminStatusBadge">Active version: {pack?.activeVersion || "none"}</span>
          <span className="adminStatusBadge">Revision: {pack?.revision || 0}</span>
          {dirty && <span className="adminWaitingStatus">Unsaved changes</span>}
        </div>
      </div>
      {selected && <p className="adminFieldHint">Version {selected.version} saved {utc(selected.createdAt)} UTC by {selected.createdBy}. Editing creates a new immutable version.</p>}
      {complete && !isEligibleReferenceVersion(selected) && <p className="adminAlert adminAlertWarning" role="status">
        This version has unverified setup images and cannot be used for automatic comparison. Capture those slots again from the chart and save a new version.
      </p>}
      <p className="adminFieldHint">Upload non-interlaced PNG or JPEG images up to 2 MiB each, 4096 pixels per side and 4 million pixels total. Uploads are privately staged and normalized to PNG. Partial drafts can be saved; activation requires all six images.</p>
      <form className="adminConfigurationForm" onSubmit={(event) => { event.preventDefault(); save(); }}>
        <div className="adminReferenceSlots">
          {SLOTS.map((slot) => <fieldset key={slot} className="adminFieldset adminReferenceSlot" disabled={mutationsBlocked}>
            <legend>{slot} · {slot.startsWith("S") ? "Successful example" : "Failed example"}</legend>
            <h4>{draft[slot]?.capturePolicy ? "Setup · comparison image" : "Reference image"}</h4>
            <ReferencePreview api={api} imageId={draft[slot]?.imageId} slot={slot}
              view={draft[slot]?.capturePolicy ? "Setup" : "Reference image"} />
            {draft[slot]?.capturePolicy
              ? <p className="adminFieldHint">Setup as of {utc(draft[slot].decisionTime)} UTC. {draft[slot].capturePolicy === "as-of-htf-v2"
                ? "The higher chart includes its developing candle, when applicable, reconstructed only from base candles completed by this time."
                : "Both timeframes use only candles completed by this time."}</p>
              : draft[slot]?.imageId && <p className="adminFieldHint">This image has no verified time cutoff. Capture it again to create separate setup and outcome views.</p>}
            {draft[slot]?.outcomeImageId && <>
              <h4>Outcome · review only</h4>
              <ReferencePreview api={api} imageId={draft[slot].outcomeImageId} slot={slot} view="Outcome" />
            </>}
            <button type="button" className="adminSecondaryButton" onClick={() => {
              onSavingChange(true); setCaptureSlot(slot);
            }}>Capture {slot} from chart</button>
            <label className="adminFormField"><span>{uploading === slot ? "Uploading…" : draft[slot] ? "Replace image" : "Upload image"}</span>
              <input type="file" accept="image/png,image/jpeg" aria-label={`${slot} image`}
                onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; upload(slot, file); }} />
            </label>
            {draft[slot] && <>
              <div className="adminReferenceMetadata">
                {METADATA.map(([field, label, maximum]) => <label key={field} className={`adminFormField${field === "note" || field === "outcome" ? " adminReferenceWide" : ""}`}>
                  <span>{label}</span>
                  {field === "note" ? <textarea rows="3" maxLength={maximum} value={draft[slot][field] || ""} aria-label={`${slot} ${label}`}
                    onChange={(event) => editMetadata(slot, field, event.target.value)} />
                    : <input type={field === "eventDate" ? "date" : "text"} maxLength={maximum}
                      inputMode={field === "mfe" || field === "mae" ? "decimal" : undefined}
                      value={draft[slot][field] || ""} aria-label={`${slot} ${label}`}
                      onChange={(event) => editMetadata(slot, field, event.target.value)} />}
                </label>)}
              </div>
              <button type="button" className="adminSecondaryButton" onClick={() => {
                setDraft((current) => { const next = { ...current }; delete next[slot]; return next; }); setMessage("");
              }}>Remove {slot} from draft</button>
            </>}
          </fieldset>)}
        </div>
        {validationError && <p className="adminAlert adminAlertError" role="alert">{validationError}</p>}
        <div className="adminFormActions">
          <button type="button" className="adminSecondaryButton" disabled={loading || saving || !!captureSlot} onClick={onRefresh}>Reload latest packs</button>
          <button type="submit" className="adminPrimaryButton" disabled={mutationsBlocked || (!dirty && !(retired && selected)) || !!validationError}>
            {saving && !uploading ? "Saving…" : retired && !dirty ? "Copy to new version" : "Save new version"}
          </button>
        </div>
      </form>
      {pack && <section className="adminStatusSection" aria-label="Reference pack activation">
        <p className="adminFieldHint">Save or discard edits before changing status. Activating a complete version enables the pack and retires its previous active version. Retiring the active version disables the pack. Retired versions remain available for viewing and copying into a new version.</p>
        <div className="adminStatusRow">
          <button type="button" className="adminSecondaryButton" disabled={mutationsBlocked || dirty || !complete || retired || (pack.activeVersion === selected?.version && pack.enabled)} onClick={activate}>Activate version {selectedVersion}</button>
          <button type="button" className="adminSecondaryButton" disabled={mutationsBlocked || dirty || (!pack.enabled && !pack.activeVersion)} onClick={toggleEnabled}>{pack.enabled ? "Disable pack" : "Enable pack"}</button>
          <button type="button" className="adminSecondaryButton" disabled={mutationsBlocked || dirty || !selected || retired} onClick={retire}>Retire version {selectedVersion}</button>
        </div>
      </section>}
      {message && <p role="status">{message}</p>}
      {pack && <section className="adminStatusSection" aria-label="Reference pack history">
        <h3>Pack history</h3>
        {pack.history.length ? <div className="adminTableViewport"><table className="adminAuditTable">
          <thead><tr><th scope="col">Time (UTC)</th><th scope="col">Action</th><th scope="col">Administrator</th><th scope="col">Version</th></tr></thead>
          <tbody>{[...pack.history].sort((a, b) => new Date(b.at) - new Date(a.at)).map((entry, index) => <tr key={`${entry.at}-${index}`}>
            <td>{utc(entry.at)}</td><td>{entry.action}</td><td>{entry.actor}</td><td>{entry.version ?? "—"}</td>
          </tr>)}</tbody>
        </table></div> : <p>No history recorded.</p>}
      </section>}
    </>}
    {loading && <button type="button" className="adminSecondaryButton" disabled>Reload latest packs</button>}
    {captureSlot && <ReferenceChartPopup slot={captureSlot} timeframe={key.timeframe}
      initialSymbol={draft[captureSlot]?.sourceSymbol} initialDate={draft[captureSlot]?.eventDate}
      initialDecisionTime={draft[captureSlot]?.decisionTime}
      onSave={saveCapture} onClose={() => setCaptureSlot(null)} />}
  </div>;
}
